import React, { useMemo } from 'react';
import { Box, Typography, Button } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { ChartTableProductsProps, DataRow, Item } from './types';
import { transformData, convertUnits, useGetFilteredItemsByClass } from './Chart.Common';
import { exportToExcel, exportToExcelSelectedRows } from "../../utils/export_xlsx";
import PieChartIcon from "@mui/icons-material/PieChart";

const ChartTableProducts: React.FC<ChartTableProductsProps> = ({
                                                                   data,
                                                                   selectedProducts,
                                                                   setSelectedProducts,
                                                                   compoundFilter,
                                                                   setCompoundFilter,
                                                                   showNonNumeric,
                                                                   setShowNonNumeric,
                                                                   selectedClass,
                                                                   setSelectedClass,
                                                                   selectedUnit,
                                                                   setSelectedUnit,
                                                                   selectedDenominator,
                                                                   setSelectedDenominator,
                                                                   selectedDiseaseCategory,
                                                                   setSelectedDiseaseCategory,
                                                                   diseaseInchiKeys,
                                                                   setDiseaseInchiKeys,
                                                                   selectedCompounds
                                                               }) => {
    // Filter items by HEADER
    const filteredItems = useMemo(() =>
            data.filter((item: Item<DataRow>) => item.HEADER === "List of Products Analysis Values"),
        [data]
    );

    // Convert units
    const convertedItems = useMemo(() =>
            convertUnits(filteredItems, selectedUnit, selectedDenominator),
        [filteredItems, selectedUnit, selectedDenominator]
    );

    // Filter by class
    const classFilteredItems = useGetFilteredItemsByClass(convertedItems, selectedClass);

    // Process items
    const processedItems = useMemo(() => {
        if (selectedProducts.length === 0) return classFilteredItems;

        const productItems: Item<DataRow>[] = [];
        classFilteredItems.forEach((item: Item<DataRow>) => {
            const productDataMap = new Map<string, DataRow[]>();
            item.DATA.forEach((row: DataRow) => {
                const key = `${row.PRODUCT || 'Unknown'} (${row.ANALYSIS_TYPE || 'Unknown'})`;
                if (selectedProducts.includes(key)) {
                    if (!productDataMap.has(key)) {
                        productDataMap.set(key, []);
                    }
                    productDataMap.get(key)!.push(row);
                }
            });

            productDataMap.forEach((data, key) => {
                productItems.push({
                    ...item,
                    DATA: data,
                    TITLE: key
                });
            });
        });

        console.log('Processed Items:', productItems);
        return productItems;
    }, [classFilteredItems, selectedProducts]);

    // Transform data for table
    const chartData = useMemo(() => {
        const transformed = transformData(
            processedItems,
            selectedProducts,
            'direct',
            (item: Item<DataRow>) => item.TITLE || 'Unknown',
            (row: DataRow) => Number(row.AVERAGE) || 0,
            (row: DataRow) => row.COMPOUND_NAME,
            (row: DataRow) => Number(row.STD_DEV) || null,
            (item: Item<DataRow>) => item.DATA.find(row => row.UNIT)?.UNIT || `${selectedUnit}/${selectedDenominator}`,
            compoundFilter,
            showNonNumeric,
            diseaseInchiKeys
        );

        // Apply compound filter
        if (selectedCompounds.length > 0 && !selectedCompounds.includes('All')) {
            return {
                ...transformed,
                labels: transformed.labels.filter(label => selectedCompounds.includes(label)),
                datasets: transformed.datasets.map(dataset => ({
                    ...dataset,
                    data: dataset.data.filter((_, idx) => selectedCompounds.includes(transformed.labels[idx])),
                    extras: Object.fromEntries(
                        Object.entries(dataset.extras || {}).filter(([compound]) => selectedCompounds.includes(compound))
                    )
                }))
            };
        }
        return transformed;
    }, [processedItems, selectedProducts, compoundFilter, showNonNumeric, selectedUnit, selectedDenominator, diseaseInchiKeys, selectedCompounds]);

    // Prepare table data
    const tableData = useMemo(() => {
        const rows: any[] = [];
        chartData.datasets.forEach(dataset => {
            const productKey = dataset.label;
            chartData.labels.forEach((compound, idx) => {
                const value = dataset.data[idx];
                const extra = dataset.extras?.[compound];
                if (value > 0 || (showNonNumeric && extra?.originalValue)) {
                    const processedItem = processedItems.find((item: Item<DataRow>) =>
                        item.TITLE === productKey
                    );
                    const rowData = processedItem?.DATA.find((row: DataRow) => row.COMPOUND_NAME === compound);
                    rows.push({
                        COMPOUND_NAME: compound,
                        PRODUCT: processedItem?.DATA[0]?.PRODUCT || 'Unknown',
                        ANALYSIS_TYPE: processedItem?.DATA[0]?.ANALYSIS_TYPE || 'Unknown',
                        AVERAGE: extra?.originalValue ?? 'N/A',
                        STD_DEV: extra?.stdev ?? 'N/A',
                        LOD: rowData?.LOD ?? 'N/A',
                        LOQ: rowData?.LOQ ?? 'N/A',
                        NUMBER_OF_REPLICATES: rowData?.NUMBER_OF_REPLICATES ?? 'N/A',
                        NUMBER_OF_ITEMS_PER_REPLICATE: rowData?.NUMBER_OF_ITEMS_PER_REPLICATE ?? 'N/A',
                        UNIT: extra?.unit ?? processedItem?.DATA[0]?.UNIT ?? `${selectedUnit}/${selectedDenominator}`,
                        TEST_METHOD_CODE: processedItem?.DATA[0]?.TEST_METHOD_CODE ?? 'N/A',
                        REPORT_PROJECT_ID: processedItem?.DATA[0]?.REPORT_PROJECT_ID ?? 'N/A'
                    });
                }
            });
        });
        console.log('Table Data:', rows);
        return rows;
    }, [chartData, processedItems, showNonNumeric, selectedUnit, selectedDenominator]);

    // Define table columns
    const columns = useMemo<MRT_ColumnDef<any>[]>(
        () => [
            { accessorKey: 'COMPOUND_NAME', header: 'Compound Name' },
            { accessorKey: 'PRODUCT', header: 'Product' },
            { accessorKey: 'ANALYSIS_TYPE', header: 'Analysis Type' },
            { accessorKey: 'AVERAGE', header: 'Average' },
            { accessorKey: 'STD_DEV', header: 'Standard Deviation' },
            { accessorKey: 'LOD', header: 'LOD' },
            { accessorKey: 'LOQ', header: 'LOQ' },
            { accessorKey: 'NUMBER_OF_REPLICATES', header: 'Replicates' },
            { accessorKey: 'NUMBER_OF_ITEMS_PER_REPLICATE', header: 'Items per Replicate' },
            { accessorKey: 'UNIT', header: 'Unit' },
            { accessorKey: 'TEST_METHOD_CODE', header: 'Test Method Code' },
            { accessorKey: 'REPORT_PROJECT_ID', header: 'Report Project ID' }
        ],
        []
    );

    return (
        <Box sx={{ p: 2 }}>
            {tableData.length > 0 ? (
                <MaterialReactTable
                    columns={columns}
                    data={tableData}
                    enableRowSelection
                    muiTableContainerProps={{
                        sx: {
                            maxHeight: '590px',
                            overflow: 'auto',
                        },
                    }}
                    renderTopToolbarCustomActions={({ table }) => (
                        <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
                            <Button
                                variant="contained"
                                onClick={() => exportToExcel(tableData, 'Product_Analysis_Data')}
                                startIcon={<PieChartIcon />}
                            >
                                Export All
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
                                    exportToExcelSelectedRows(selectedRows, 'Selected_Product_Analysis_Data');
                                }}
                                disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                                startIcon={<PieChartIcon />}
                            >
                                Export Selected
                            </Button>
                        </Box>
                    )}
                />
            ) : (
                <Typography>No valid product analysis data available for selected filters.</Typography>
            )}
        </Box>
    );
};

export default ChartTableProducts;
